// src/pages/ProjectDetailsPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, ChevronRight, Github, ExternalLink } from 'lucide-react';
import { projects } from '../models/portfolioData';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

// App Store SVG icon
function AppStoreIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
    </svg>
  );
}

// Play Store SVG icon
function PlayStoreIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
      <path d="M3 20.5v-17c0-.83.94-1.3 1.6-.8l14 8.5c.6.36.6 1.24 0 1.6l-14 8.5c-.66.5-1.6.03-1.6-.8zm2-13.7v11.4L17 12 5 6.8z"/>
    </svg>
  );
}

export default function ProjectDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeMedia, setActiveMedia] = useState(0);

  const project = projects.find((p) => String(p.id) === String(id));
  const currentIndex = projects.findIndex((p) => String(p.id) === String(id));
  const prevProject = currentIndex > 0 ? projects[currentIndex - 1] : null;
  const nextProject = currentIndex < projects.length - 1 ? projects[currentIndex + 1] : null;

  // Scroll to top on id change
  useEffect(() => {
    window.scrollTo(0, 0);
    setActiveMedia(0);
  }, [id]);

  if (!project) {
    return (
      <div className="min-h-screen bg-white dark:bg-[#12121a] flex flex-col justify-between">
        <Navbar />
        <div className="container text-center py-32">
          <h2 className="font-heading text-3xl text-primary mb-4">Project Not Found</h2>
          <p className="text-gray-500 mb-6">The project you are looking for does not exist or has been removed.</p>
          <Link to="/#portfolio" className="btn btn-primary rounded">
            <ArrowLeft size={16} className="mr-2 inline" /> Back to Portfolio
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const mediaList = project.media || [];
  const currentMediaItem = mediaList[activeMedia];

  return (
    <div className="min-h-screen bg-white dark:bg-[#12121a] text-[#393e46] dark:text-[#e1dfed] font-body transition-colors duration-300">
      <Navbar />

      {/* Meyawo Header Mini */}
      <header className="header-mini">
        <h1 className="header-title">{project.title}</h1>
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-divider">·</li>
            <li className="breadcrumb-item">
              <Link to="/#portfolio">Portfolio</Link>
            </li>
            <li className="breadcrumb-divider">·</li>
            <li className="breadcrumb-item active" aria-current="page">
              {project.title}
            </li>
          </ol>
        </nav>
      </header>

      {/* Main Content Area */}
      <div className="container py-10 md:py-14">
        {/* Top Back Action Bar */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <button
            onClick={() => navigate('/#portfolio')}
            className="btn btn-outline-primary btn-sm rounded shadow-sm inline-flex items-center"
          >
            <ArrowLeft size={15} className="mr-2" /> Back to Projects
          </button>

          <div className="flex items-center gap-2">
            <span className="bg-purple-100 dark:bg-[#232334] text-primary dark:text-[#a59cca] font-semibold text-xs px-3 py-1 rounded-full border border-purple-200 dark:border-[#2f2f45]">
              Category: {project.category}
            </span>
          </div>
        </div>

        {/* ─── MEDIA SHOWCASE (HERO) ─── */}
        {mediaList.length > 0 && (
          <div className="mb-12 bg-gray-50 dark:bg-[#171722] p-4 md:p-6 rounded border border-gray-200 dark:border-[#272738] shadow-sm">
            {/* Active Display */}
            <div
              className={`w-full rounded overflow-hidden bg-gradient-to-br ${
                project.gradient || 'from-indigo-900 to-purple-950'
              } flex items-center justify-center`}
              style={{ minHeight: '360px', maxHeight: '560px' }}
            >
              {currentMediaItem?.type === 'video' ? (
                <video
                  key={currentMediaItem.url}
                  src={currentMediaItem.url}
                  controls
                  className="w-full h-full max-h-[560px] object-contain"
                />
              ) : (
                <img
                  key={currentMediaItem?.url}
                  src={currentMediaItem?.url}
                  alt={currentMediaItem?.alt || project.title}
                  className="w-full h-full max-h-[560px] object-contain transition-all duration-300"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              )}
            </div>

            {/* Thumbnail Switcher */}
            {mediaList.length > 1 && (
              <div className="flex gap-3 mt-4 overflow-x-auto pb-2 justify-center">
                {mediaList.map((m, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveMedia(i)}
                    className={`flex-shrink-0 rounded overflow-hidden border-2 transition-all duration-200 select-none ${
                      activeMedia === i
                        ? 'border-primary shadow-md scale-105'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-400 opacity-70 hover:opacity-100'
                    }`}
                    style={{ width: '90px', height: '60px' }}
                    title={`View image ${i + 1}`}
                  >
                    {m.type === 'video' ? (
                      <div
                        className={`w-full h-full bg-gradient-to-br ${
                          project.gradient || 'from-indigo-900 to-purple-950'
                        } flex items-center justify-center`}
                      >
                        <span className="text-white text-xs">▶ Video</span>
                      </div>
                    ) : (
                      <img
                        src={m.thumb || m.url}
                        alt={m.alt || `Thumbnail ${i + 1}`}
                        className="w-full h-full object-cover"
                      />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ─── TWO-COLUMN DETAILS & OVERVIEW ─── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Main Column (2 spans) */}
          <div className="lg:col-span-2 space-y-8">
            {/* Overview */}
            <div>
              <h3 className="font-heading text-2xl text-primary font-bold mb-3">
                Project Overview
              </h3>
              <p className="text-gray-700 dark:text-gray-300 text-base leading-relaxed mb-4">
                {project.longDesc || project.description}
              </p>
            </div>

            {/* Key Features */}
            {project.features && project.features.length > 0 && (
              <div className="bg-white dark:bg-[#171722] border border-gray-200 dark:border-[#272738] rounded p-6 shadow-sm">
                <h4 className="font-heading text-xl text-primary font-bold mb-4">
                  Key Features & Capabilities
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {project.features.map((feature) => (
                    <div
                      key={feature}
                      className="flex items-start gap-2.5 text-sm text-gray-700 dark:text-gray-300"
                    >
                      <ChevronRight
                        size={16}
                        className="text-primary mt-0.5 flex-shrink-0"
                      />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Technical Challenges Solved */}
            {project.challenges && (
              <div className="bg-purple-50/60 dark:bg-[#1a1a28] border-l-4 border-primary p-6 rounded-r shadow-sm">
                <h4 className="font-heading text-lg text-primary font-bold mb-2">
                  Technical Challenge & Solution
                </h4>
                <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                  {project.challenges}
                </p>
              </div>
            )}
          </div>

          {/* Sidebar Column (1 span) */}
          <div className="space-y-6">
            {/* Metadata Card */}
            <div className="bg-white dark:bg-[#171722] border border-gray-200 dark:border-[#272738] rounded p-6 shadow-sm">
              <h4 className="font-heading text-lg text-primary font-bold mb-4 pb-2 border-b border-gray-100 dark:border-[#272738]">
                Project Metadata
              </h4>

              <div className="space-y-4 text-sm">
                <div>
                  <span className="block text-xs uppercase font-semibold text-gray-400">
                    Category
                  </span>
                  <span className="font-medium text-gray-800 dark:text-gray-200">
                    {project.category}
                  </span>
                </div>

                <div>
                  <span className="block text-xs uppercase font-semibold text-gray-400">
                    Target Platforms
                  </span>
                  <span className="font-medium text-gray-800 dark:text-gray-200">
                    iOS & Android
                  </span>
                </div>

                <div>
                  <span className="block text-xs uppercase font-semibold text-gray-400 mb-2">
                    Technologies Used
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {project.tech.map((t) => (
                      <span
                        key={t}
                        className="bg-gray-100 dark:bg-[#232334] text-gray-700 dark:text-gray-300 text-xs px-2.5 py-1 rounded border border-gray-200 dark:border-[#2e2e42]"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-8 pt-6 border-t border-gray-100 dark:border-[#272738] space-y-3">
                <span className="block text-xs uppercase font-semibold text-gray-400 mb-2">
                  Live Project Links
                </span>

                {project.caseStudySlug && (
                  <Link
                    to={`/case-study/${project.caseStudySlug}`}
                    className="btn btn-outline-primary btn-sm rounded w-full flex items-center justify-center gap-2"
                  >
                    <span>Read Case Study</span>
                  </Link>
                )}

                {project.pubdev && (
                  <a
                    href={project.pubdev}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-outline-primary btn-sm rounded w-full flex items-center justify-center gap-2"
                  >
                    <ExternalLink size={15} />
                    <span>View on pub.dev</span>
                  </a>
                )}

                {project.github && (
                  <a
                    href={project.github}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-primary btn-sm rounded w-full flex items-center justify-center gap-2"
                  >
                    <Github size={15} />
                    <span>View GitHub Repo</span>
                  </a>
                )}

                {project.appstore && (
                  <a
                    href={project.appstore}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-outline-primary btn-sm rounded w-full flex items-center justify-center gap-2"
                  >
                    <AppStoreIcon />
                    <span>Download on App Store</span>
                  </a>
                )}

                {project.playstore && (
                  <a
                    href={project.playstore}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-outline-primary btn-sm rounded w-full flex items-center justify-center gap-2"
                  >
                    <PlayStoreIcon />
                    <span>Get on Google Play</span>
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ─── BOTTOM PREV / NEXT NAVIGATION ─── */}
        <div className="mt-16 pt-8 border-t border-gray-200 dark:border-[#272738] flex flex-col sm:flex-row items-center justify-between gap-4">
          {prevProject ? (
            <Link
              to={`/project/${prevProject.id}`}
              className="text-left group flex items-center gap-3 p-3 rounded hover:bg-gray-50 dark:hover:bg-[#171722] transition-colors"
            >
              <div className="w-9 h-9 rounded-full border border-gray-300 dark:border-gray-600 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white group-hover:border-primary transition-all">
                ←
              </div>
              <div>
                <span className="block text-xs text-gray-400">Previous Project</span>
                <span className="font-heading text-sm text-primary font-semibold group-hover:underline">
                  {prevProject.title}
                </span>
              </div>
            </Link>
          ) : (
            <div></div>
          )}

          <Link
            to="/#portfolio"
            className="btn btn-outline-primary btn-sm rounded shadow-sm"
          >
            All Projects
          </Link>

          {nextProject ? (
            <Link
              to={`/project/${nextProject.id}`}
              className="text-right group flex items-center gap-3 p-3 rounded hover:bg-gray-50 dark:hover:bg-[#171722] transition-colors"
            >
              <div>
                <span className="block text-xs text-gray-400">Next Project</span>
                <span className="font-heading text-sm text-primary font-semibold group-hover:underline">
                  {nextProject.title}
                </span>
              </div>
              <div className="w-9 h-9 rounded-full border border-gray-300 dark:border-gray-600 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white group-hover:border-primary transition-all">
                →
              </div>
            </Link>
          ) : (
            <div></div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
